import { useState } from 'react';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Checkbox, FormControlLabel, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { DEFAULT_WELCOME_MESSAGE, DEFAULT_UNAVAILABLE_MESSAGE } from 'constants/data';
import { CheckboxCheckedIcon, CheckboxIcon } from 'assets';
import { FormInput, FormMediaUpload, FormSelector, Info } from 'components';
import { ApiUser, UpdateClinicUserRequest, UpdateUserRequest } from 'types';
import { getSuffixesOptionsForUser, getTitlesOptionsForUser, isPreferredDisplayNameValid } from 'utils';
import { useAppSelector } from 'store';
import { selectUser } from 'store/user';
import { isHipaaLink } from '../../../../config/productProfile';

import styles from './CreateRoomForm.module.scss';

type CreateWaitingRoomFormType = {
  user: UpdateUserRequest;
  clinicUser: UpdateClinicUserRequest;
  organization: string;
};

type CreateRoomFormProps = {
  onSubmit(
    profilePicture: File | null,
    profilePictureChanged: boolean,
    user: UpdateUserRequest,
    clinicUser: UpdateClinicUserRequest,
    organization: string,
  ): Promise<void>;
};

export function CreateRoomForm({ onSubmit }: CreateRoomFormProps) {
  const { t } = useTranslation('auth');
  const user: ApiUser = useAppSelector(selectUser)!;

  const schema = Yup.object().shape({
    clinicUser: Yup.object().shape({
      roomName: Yup.string()
        .required(t('validationRoomUrlRequired'))
        .matches(/^(([a-z]|\d|_)+([a-z]|\d|-|_)*)$/i, t('validationRoomUrlInvalidChars'))
        .max(128, t('validationMaxLength', { count: 128 })),
      welcomeMessage: Yup.string()
        .max(500, t('validationMaxLength', { count: 500 }))
        .nullable(),
      unavailableMessage: Yup.string()
        .max(100, t('validationMaxLength', { count: 100 }))
        .nullable(),
      preferedDisplayName: Yup.string()
        .max(50, t('validationMaxLength', { count: 50 }))
        .test('preferred-display-name-valid', t('validationPreferredDisplayNameInvalidChars'), (value) =>
          isPreferredDisplayNameValid(value),
        )
        .nullable()
        .label(t('preferredDisplayName')),
      biography: Yup.string()
        .max(2000, t('validationMaxLength', { count: 2000 }))
        .nullable(),
      displayEmail: Yup.boolean(),
      suffix: Yup.string().nullable(),
    }),
    user: Yup.object().shape({
      title: Yup.string().nullable(),
      speciality: Yup.string()
        .max(25, t('validationMaxLength', { count: 25 }))
        .nullable(),
    }),
    organization: Yup.string()
      .max(100, t('validationMaxLength', { count: 100 }))
      .required(t('validationRequired')),
  });

  const form = useForm<CreateWaitingRoomFormType>({
    resolver: yupResolver(schema),
    defaultValues: {
      user: {
        title: user.title,
        speciality: user.speciality,
      },
    },
    mode: 'all',
  });

  const [profilePicture, setProfilePicture] = useState<File | null>(null);
  const [profilePictureChanged, setProfilePictureChanged] = useState<boolean>(false);

  const handleSubmit = async (): Promise<void> => {
    if (!form.formState.isValid) {
      return;
    }

    const formValues: CreateWaitingRoomFormType = form.getValues();

    await onSubmit(
      profilePicture,
      profilePictureChanged,
      formValues.user,
      formValues.clinicUser,
      formValues.organization,
    );
  };

  const handlePictureChanged = (file: File): void => {
    setProfilePicture(file);
    setProfilePictureChanged(true);
  };

  const handlePictureDeleted = (): void => {
    setProfilePicture(null);
    setProfilePictureChanged(true);
  };

  return (
    <div className={styles.form}>
      <Stack className={styles.row}>
        <Stack direction='row' className={styles.labelWithInfo}>
          <Typography component='p' variant='body2'>
            {isHipaaLink ? t('providerOrgName') : t('orgName')}
            <span className={styles.asterisk}>*</span>
          </Typography>
          <Info
            children={<Typography variant='body6'>{isHipaaLink ? t('providerOrgInfo') : t('orgInfo')}</Typography>}
          />
        </Stack>
        <FormInput name='organization' control={form.control} />
      </Stack>

      <Stack className={styles.row}>
        <Stack direction='row' className={styles.labelWithInfo}>
          <Typography component='p' variant='body2'>
            {t('roomNameUrl')} <span className={styles.asterisk}>*</span>
          </Typography>
          <Info text={t('roomNameInfo')} />
        </Stack>
        <Stack direction='row' alignItems='center' className={styles.roomNameContainer}>
          <div className={styles.roomNameLink}>
            <Typography variant='body1' color='var(--app-text-muted)'>
              {window.location.origin}/
            </Typography>
          </div>
          <FormInput
            placeholder={t('roomNamePlaceholder')}
            name='clinicUser.roomName'
            control={form.control}
            className={styles.roomName}
            errorHelperClassName={styles.roomNameError}
          />
        </Stack>
      </Stack>

      <Stack className={styles.row}>
        <Stack direction='row' className={styles.labelWithInfo}>
          <Typography component='p' variant='body2'>
            {t('welcomeMessage')}
          </Typography>
          <Info text={isHipaaLink ? t('welcomeMessageInfo') : t('welcomeMessageInfoGuest')} />
        </Stack>
        <FormInput
          name='clinicUser.welcomeMessage'
          control={form.control}
          multiline
          maxRows={4}
          placeholder={t(DEFAULT_WELCOME_MESSAGE)}
        />
      </Stack>

      <Stack className={styles.row}>
        <Stack direction='row' className={styles.labelWithInfo}>
          <Typography component='p' variant='body2'>
            {t('unavailableMessage')}
          </Typography>
          <Info text={isHipaaLink ? t('unavailableMessageInfo') : t('unavailableMessageInfoGuest')} />
        </Stack>
        <FormInput
          name='clinicUser.unavailableMessage'
          control={form.control}
          multiline
          maxRows={4}
          placeholder={t(DEFAULT_UNAVAILABLE_MESSAGE)}
        />
      </Stack>

      <Stack className={styles.row}>
        <Stack direction='row' alignItems='flex-end'>
          <Typography component='p' variant='body2'>
            {t('profilePicture')}
          </Typography>
        </Stack>
        <FormMediaUpload
          onInputChange={handlePictureChanged}
          onRemove={handlePictureDeleted}
          title={t('profilePicture')}
          src={user.linkToProfilePicture}
        />
      </Stack>

      <Stack className={styles.row}>
        <Stack direction='row' alignItems='center'>
          <Typography component='p' variant='body2'>
            {t('titleLabel')}
          </Typography>
        </Stack>
        <FormSelector variant='outlined' name='user.title' control={form.control} options={getTitlesOptionsForUser()} />
      </Stack>

      {isHipaaLink && (
        <Stack className={styles.row}>
          <Stack direction='row' alignItems='center'>
            <Typography component='p' variant='body2'>
              {t('suffixLabel')}
            </Typography>
          </Stack>
          <FormSelector
            variant='outlined'
            name='clinicUser.suffix'
            control={form.control}
            options={getSuffixesOptionsForUser()}
          />
        </Stack>
      )}

      <Stack className={styles.row}>
        <Stack direction='row' className={styles.labelWithInfo}>
          <Typography component='p' variant='body2'>
            {t('preferredDisplayName')}
          </Typography>
          <Info text={isHipaaLink ? t('preferredDisplayNameInfo') : t('preferredDisplayNameInfoGuest')} />
        </Stack>
        <FormInput
          name='clinicUser.preferedDisplayName'
          control={form.control}
          errorHelperClassName={styles.errorHelper}
        />
      </Stack>

      <Stack className={styles.row}>
        <FormControlLabel
          control={
            <Checkbox
              className={styles.checkbox}
              {...form.register('clinicUser.displayEmail')}
              icon={<CheckboxIcon className={styles.checkboxIcon} />}
              checkedIcon={<CheckboxCheckedIcon className={styles.checkboxIcon} />}
            />
          }
          label={t('displayEmailInWaitingRoom')}
        />
      </Stack>

      <Stack className={styles.row}>
        <Stack direction='row' spacing={1} alignItems='center'>
          <Typography component='p' variant='body2'>
            {t('personalBiography')}
          </Typography>
        </Stack>
        <FormInput name='clinicUser.biography' className={styles.bio} control={form.control} multiline maxRows={20} />
      </Stack>

      <Stack className={styles.row} alignItems='flex-end'>
        <Button
          id='CreateRoomFormNext'
          variant='contained'
          onClick={handleSubmit}
          disabled={!form.formState.isValid}
          className={styles.formButton}
          type='submit'
        >
          {t('saveAndContinue')}
        </Button>
      </Stack>
    </div>
  );
}
